import { useState } from 'react';
import { WriteOnly, useCanWrite } from '@core/auth/access';
import { Menu, type MenuItem } from '@core/ui/Menu';
import { Ban, Check, Crosshair, MessageCircleQuestion, SquarePen, Star, StarOff, TriangleAlert } from 'lucide-react';
import { cn } from '@core/ui/cn';
import { Avatar } from '@core/ui/Avatar';
import { ReleaseMarks, taskTipLines } from '../shared/TaskBar';
import { Tooltip } from '@core/ui/Tooltip';
import { dateRange } from '@core/dates';
import { MAX_ASSIGNEES, type Attention, type Task } from '../shared/data';
import { STATUS_LOOK } from '../../launch/planner';
import type { LaunchRelease } from './planner';

export type NameColor = { name: string; color: string };
export type Person = NameColor;
export type ProjectInfo = NameColor;
export type CardActions = {
  confirm: (t: Task) => void;   // ✓ — уверенность confirmed / снять
  atRisk: (t: Task) => void;    // ! — at_risk / снять
  star: (t: Task) => void;      // ★ — ключевой релиз / снять
  drop: (t: Task) => void;      // × — «Not deploying»; вернуть — Deploy в карточке задачи
  open: (t: Task, tab?: 'task' | 'questions') => void;
  jump: (t: Task) => void;      // Show on the grid — на Projects к бару
};

/** Меню релиза — те же действия, что кнопки ✓ ! ★ × по наведению, плюс карточка, вопрос и прыжок на сетку. */
export function releaseMenu(t: Task, a: CardActions, canWrite: boolean): MenuItem[] {
  const read: MenuItem[] = [
    { label: 'Open', onSelect: () => a.open(t), icon: SquarePen },
    { label: 'Ask a question', onSelect: () => a.open(t, 'questions'), icon: MessageCircleQuestion },
    { label: 'Show on the grid', onSelect: () => a.jump(t), icon: Crosshair },
  ];
  if (!canWrite) return read;
  return [
    ...read, 'sep',
    { label: t.key_release ? 'Not a key release' : 'Key release ★', onSelect: () => a.star(t), icon: t.key_release ? StarOff : Star },
    { label: t.release_confidence === 'confirmed' ? 'Remove «Confirmed»' : 'Confirmed ✓', onSelect: () => a.confirm(t), icon: Check },
    { label: t.release_confidence === 'at_risk' ? 'Remove «At risk»' : 'At risk !', onSelect: () => a.atRisk(t), icon: TriangleAlert },
    'sep',
    { label: 'Not deploying — remove from the plan', onSelect: () => a.drop(t), danger: true, icon: Ban },
  ];
}

const actionBtn = 'min-h-0 w-7 h-7 rounded text-sm font-bold leading-none border border-transparent hover:bg-gray-100';
/** Ключевой релиз — жёлтая карточка и чип; остальные белые. */
export const KEY_RELEASE_CLASS = (key: boolean) => (key ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200');

/**
 * Карточка релиза, как в старом Release Plan: полоса цвета проекта слева, название целиком, проект и
 * исполнители; действия по наведению. Двойной клик — карточка задачи, та же, что в Projects.
 */
export function ReleaseCard({ task, project, people, attention, actions, onOpen }: { task: Task; project: ProjectInfo; people: Person[]; attention?: Attention | undefined; actions: CardActions; onOpen: (t: Task) => void }) {
  const [confirmDrop, setConfirmDrop] = useState(false);
  const canWrite = useCanWrite();
  return (
    <Menu items={releaseMenu(task, actions, canWrite)}>
    <Tooltip title={task.title} lines={[project.name, ...taskTipLines(task, people.map((p) => p.name))]} hint="Drag to another week · double-click to open">
    <div
      data-release={task.id} data-key-release={task.key_release || undefined} data-attention={attention ? attention.name : undefined} role="button" tabIndex={0}
      className={cn('group relative rounded-md border shadow-sm px-3 py-2 flex flex-col gap-1 min-w-0 hover:shadow focus:outline-none focus-visible:ring-2 ring-accent/70', KEY_RELEASE_CLASS(task.key_release))}
      style={{ borderLeft: `4px solid ${project.color}`, outline: attention ? `2px solid ${attention.color}` : undefined, outlineOffset: 2 }}
      onDoubleClick={() => onOpen(task)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(task); }}
    >
      <div className="font-semibold text-base leading-snug pr-24" data-title>
        <ReleaseMarks task={task} size="w-5 h-5 text-xs" />{task.title}
      </div>
      <div className="flex items-center gap-2 text-sm min-w-0">
        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: project.color }} />
        <span className="uppercase tracking-wide text-xs font-semibold truncate" style={{ color: project.color }}>{project.name}</span>
        <span className="ml-auto flex items-center gap-1 text-gray-600 shrink-0">
          <span className="truncate max-w-40">{people.map((p) => p.name).join(', ') || 'Unassigned'}</span>
          <span className="flex -space-x-1">{people.slice(0, MAX_ASSIGNEES).map((p, i) => <Avatar key={i} name={p.name} color={p.color} size={20} className="ring-1 ring-white" />)}</span>
        </span>
      </div>
      {/* действия — по наведению, в правом верхнем углу; × спрашивает на месте */}
      <WriteOnly><div className={cn('absolute top-1 right-1 flex items-center gap-0.5 bg-white/95 rounded', confirmDrop ? '' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100')} onDoubleClick={(e) => e.stopPropagation()}>
        {confirmDrop ? (
          <>
            <span className="text-xs font-medium px-1">Not deploying?</span>
            <button type="button" className={`${actionBtn} text-danger`} onClick={() => { setConfirmDrop(false); actions.drop(task); }}>Yes</button>
            <button type="button" className={actionBtn} onClick={() => setConfirmDrop(false)}>No</button>
          </>
        ) : (
          <>
            <button type="button" aria-pressed={task.release_confidence === 'confirmed'} className={cn(actionBtn, task.release_confidence === 'confirmed' ? 'text-green-700 bg-green-50 border-green-300' : 'text-gray-500')} title="Confirmed" aria-label="Confirmed" onClick={() => actions.confirm(task)}>✓</button>
            <button type="button" aria-pressed={task.release_confidence === 'at_risk'} className={cn(actionBtn, task.release_confidence === 'at_risk' ? 'text-red-700 bg-red-50 border-red-300' : 'text-gray-500')} title="At risk" aria-label="At risk" onClick={() => actions.atRisk(task)}>!</button>
            <button type="button" aria-pressed={task.key_release} className={cn(actionBtn, task.key_release ? 'text-amber-600 bg-amber-50 border-amber-300' : 'text-gray-500')} title="Key release" aria-label="Key release" onClick={() => actions.star(task)}>★</button>
            <button type="button" className={`${actionBtn} text-gray-500 hover:text-danger`} title="Not deploying — remove from the plan" aria-label="Remove from the plan" onClick={() => setConfirmDrop(true)}>×</button>
          </>
        )}
      </div></WriteOnly>
    </div>
    </Tooltip>
    </Menu>
  );
}

/**
 * Релиз лонча: та же рамка, полоса и пилюля цвета статуса release; имя лонча только в тултипе — названия лончей
 * длинные. Без действий ✓ ! ★ × и без протяжки: дата меняется на Launch Board или в карточке (двойной клик).
 */
export function LaunchReleaseCard({ release, onOpen }: { release: LaunchRelease; onOpen: (r: LaunchRelease) => void }) {
  const look = STATUS_LOOK.release;
  return (
    <Menu items={[{ label: 'Open the launch task', onSelect: () => onOpen(release), icon: SquarePen }]}>
    <Tooltip title={release.title} lines={[release.launch.name, dateRange(release.task.start_date, release.task.end_date)]} hint="Double-click to open the launch task">
    <div
      data-release={release.id} data-launch-release role="button" tabIndex={0}
      className="rounded-md border shadow-sm px-3 py-2 flex flex-col gap-1 min-w-0 bg-white border-gray-200 hover:shadow focus:outline-none focus-visible:ring-2 ring-accent/70"
      style={{ borderLeft: `4px solid ${look.border}` }}
      onDoubleClick={() => onOpen(release)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(release); }}
    >
      <div className="font-semibold text-base leading-snug" data-title>{release.title}</div>
      <div className="flex items-center text-sm min-w-0">
        <span className="inline-flex items-center h-5 px-2 rounded-full uppercase tracking-wide text-xs font-semibold" style={{ background: look.bg, color: look.text }} data-launch-pill>Launch</span>
      </div>
    </div>
    </Tooltip>
    </Menu>
  );
}
